import { DetailRequirement, MAX_FIELD_PAGE_SIZE, TraitId, TraitStatus } from '@cibo/core'
import {
  DetailEditingProvider,
  PROFILING_QUERY_KEY,
  usePaginatedFields,
  useProfileFields,
} from '@cibo/landmanager'
import { Loading } from '@cibo/profile'
import { AddInformation, useCombinedBenchmarkRequirements, useProgramConfigs } from '@cibo/programs'
import { ResponsiveDialog, WindowLocation } from '@cibo/ui'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { LayoutSection } from 'components/ImpactLayout'
import ROUTE, { LAND_MANAGER_ROUTES } from 'navigation/types/routes'
import { uniq } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ReviewInformationNavButtons } from './ReviewFieldsInfoNav'

export interface ProfileFieldsLocationState {
  profileFieldIds?: string[]
}

export const ProfileFields = () => {
  const [isCacheClear, setIsCachedClear] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.invalidateQueries(PROFILING_QUERY_KEY).then(() => setIsCachedClear(true))
  }, [])

  return !!isCacheClear ? (
    <DetailEditingProvider>
      <ProfileFieldsImpl />
    </DetailEditingProvider>
  ) : (
    <Loading />
  )
}

const ProfileFieldsImpl = () => {
  const { t } = useTranslation('impact/ProfileFields')
  const [isReviewing, setIsReviewing] = useState<boolean>(false)
  const navigate = useNavigate()
  const programs = useProgramConfigs()
  const { state } = useLocation() as WindowLocation<ProfileFieldsLocationState>
  const { fieldId } = useParams()
  const profileFieldIds = !!fieldId ? [fieldId] : state?.profileFieldIds

  const programIds = programs.data?.map(({ programId }) => programId)
  const profileRequirements = useCombinedBenchmarkRequirements(
    'profile',
    ({ isClosedForEnrollment }) => !isClosedForEnrollment
  )

  const incompleteProfileFields = useProfileFields(
    0,
    MAX_FIELD_PAGE_SIZE,
    {
      resourceIds: profileFieldIds?.join(','),
      programIds,
      sort: [{ field: 'updatedAt', sort: 'desc' }],
    },
    PROFILING_QUERY_KEY
  )

  const remainingIncompleteProfileFields = usePaginatedFields(
    0,
    1,
    {
      resourceIds: profileFieldIds?.join(','),
      anyPrograms: programIds?.map(id => ({
        id,
        benchmarks: [{ benchmark: 'profile', status: 'incomplete' }],
      })),
      footprintResolved: true,
    },
    { enabled: !!programIds?.length, cacheTime: 300 }
  )
  const fieldsWithOpportunities = usePaginatedFields(
    0,
    1,
    {
      resourceIds: profileFieldIds?.join(','),
      anyPrograms: programIds?.map(id => ({
        id,
        benchmarks: [{ benchmark: 'profile', status: 'pass' }],
      })),
    },
    { enabled: !!programIds?.length, cacheTime: 300 }
  )
  const fieldsBase = usePaginatedFields(
    0,
    MAX_FIELD_PAGE_SIZE,
    {
      resourceIds: profileFieldIds?.join(','),
      footprintResolved: true,
      includeAllResolved: true,
    },
    { enabled: !!programIds?.length, cacheTime: 300 }
  )

  const requiredTraits = useMemo(
    () =>
      profileRequirements.data &&
      uniq(
        profileRequirements.data
          ?.map(({ traitId }) => traitId)
          .filter(traitId => traitId !== 'geometry' && traitId !== 'name')
      ),
    [profileRequirements.data]
  )

  const requirementsByTraitId = useMemo(() => {
    const requirementsById: Partial<Record<TraitId, DetailRequirement[] | undefined>> = {}
    requiredTraits?.forEach(traitId => {
      const requirements = profileRequirements.data?.filter(
        requirement => requirement.traitId === traitId
      )
      requirementsById[traitId] = requirements
    })
    return requirementsById
  }, [profileRequirements.data])

  // because this is a complex iteration on a potentially multi-hundred long list, we're just finding the first instance of a missing value and setting missing based on that
  const traitStatuses = useMemo(() => {
    const programIds = programs.data?.map(({ programId }) => programId)
    return (
      // (verify the presence of necessary data)
      remainingIncompleteProfileFields.data &&
      requiredTraits &&
      // beginning with the list of required traits
      // map over the traits.
      requiredTraits.map(
        // for each trait
        traitId => {
          const traitStatus: {
            traitId: TraitId
            missing?: boolean
          } = { traitId }
          // find the first incompletely profiled field
          remainingIncompleteProfileFields.data.items.find(field => {
            // go over the list of requirements associated with the current traitId (in practice this should be a list of each required year/trait combo)
            const fieldMissingTrait = requirementsByTraitId[traitId]?.find(requirement => {
              // and locate the first missing required detail on the field
              return field.isDetailAbsentOrIncomplete(
                !!programIds ? programIds : [],
                'profile',
                requirement.traitId,
                requirement.year
              )
            })
            // if this field is missing a trait
            if (fieldMissingTrait) {
              // set the missing value on the status and return true to let the field exit
              traitStatus.missing = true
              return true
            }
            // otherwise return false to let the find know to continue looking.
            return false
          })
          if (remainingIncompleteProfileFields.data.numAvailable === 0) {
            traitStatus.missing = false
          }
          return traitStatus
        }
      )
    )
  }, [
    incompleteProfileFields,
    remainingIncompleteProfileFields,
    requirementsByTraitId,
    requiredTraits?.join(','),
    programs.isFetched,
  ])

  useEffect(() => {
    if (requiredTraits) {
      setFocusTraitId(requiredTraits[0])
    }
  }, [requiredTraits?.join(',')])

  const [focusTraitId, setFocusTraitId] = useState<TraitId>()

  const years = useMemo(() => {
    if (!!focusTraitId && !!programs.data) {
      //get all the years from all the programs
      const years = programs.data.map(program => program.yearsForTrait('profile', focusTraitId))
      return uniq(years.flat()).filter(year => !!year) as number[]
    }
  }, [focusTraitId, programs.isFetched])

  const isLoading =
    profileRequirements.isLoading ||
    incompleteProfileFields.isLoading ||
    incompleteProfileFields.isFetching

  const onContinue = () => {
    if (isReviewing) {
      setIsReviewing(false)
    } else {
      navigate(LAND_MANAGER_ROUTES.ROOT)
    }
  }
  const onFinishLater = () => navigate(LAND_MANAGER_ROUTES.ROOT)

  if (isLoading) {
    return <Loading />
  }

  const isReviewingAlreadyProfiledFields = incompleteProfileFields.data?.numAvailable === 0

  const navState = {
    resourceIds: fieldsWithOpportunities.data?.items.map(({ resourceId }) => resourceId),
  }

  return (
    <>
      <LayoutSection autoHeight>
        <Box>
          <Typography variant="h5" color="text.secondary">
            {t('informationNeeded')}
          </Typography>
          <Typography color="text.secondary">{t('addInformation')}</Typography>
        </Box>
        <Box>
          <AddInformation
            benchmark="profile"
            detailRequirements={profileRequirements.data as DetailRequirement[]}
            fields={
              !!profileFieldIds ? fieldsBase.data?.items : incompleteProfileFields.data?.items
            }
            focusTraitId={focusTraitId}
            isLoadingData={isLoading}
            NavButtonsOverride={
              isReviewingAlreadyProfiledFields ? ReviewInformationNavButtons : undefined
            }
            onContinue={
              isReviewingAlreadyProfiledFields
                ? () =>
                    navigate(ROUTE.OPPORTUNITIES, {
                      state: navState,
                    })
                : onContinue
            }
            onFinishLater={onFinishLater}
            programIds={programIds}
            setFocusTraitId={setFocusTraitId}
            traitsLoading={profileRequirements.isLoading}
            traitsError={profileRequirements.error}
            traitStatuses={traitStatuses as TraitStatus[]}
            years={years}
            hideAddFields
            hideLegend
          />
        </Box>
      </LayoutSection>
      <ResponsiveDialog
        open={
          !isLoading &&
          !isReviewing &&
          (incompleteProfileFields.data?.numAvailable || 0) > 0 &&
          remainingIncompleteProfileFields.data?.numAvailable === 0
        }
        onClose={() => null /* would be better to close modal with certainty */}
      >
        {!fieldsWithOpportunities.data?.numAvailable ? (
          <>
            <DialogTitle>{t('doneTitle')}</DialogTitle>
            <DialogContent>
              <Typography>
                {t('doneMessage', {
                  count: profileFieldIds?.length,
                })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" component={Link} to={LAND_MANAGER_ROUTES.ROOT}>
                {t('done')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{t('seeOpportunitiesTitle')}</DialogTitle>
            <DialogContent>
              <Typography>
                {t('seeOpportunitiesMessage', {
                  count: profileFieldIds?.length,
                  opportunities: fieldsWithOpportunities.data?.numAvailable,
                })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Stack spacing={2} direction="row">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsReviewing(true)
                  }}
                >
                  {t('reviewData')}
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to={ROUTE.OPPORTUNITIES}
                  state={navState}
                >
                  {t('seeOpportunities')}
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </ResponsiveDialog>
    </>
  )
}
